import "./App.css";
import "./style/global.css";
import "./style/component/TranslationNotification.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { TranslationProvider } from "./contexts/TranslationContext";
import Layout from "./component/layout/Layout";
import HomePage from "./page/HomePage";
import Contact from "./page/Contact";
import Publications from "./page/Publications";
import OpenSource from "./page/OpenSource";
import Research from "./page/Research";
import Members from "./page/Members";
import Partner from "./page/Partner";
import Donations from "./page/Donations";
import Terms from "./page/Terms";
import Privacy from "./page/Privacy";
import Brand from "./page/Brand";
import Webinars from "./page/Webinars.jsx";
import AdminLogin from "./page/admin/AdminLogin";
import AdminDashboard from "./page/admin/AdminDashboard";
import { useLanguageFeatures, useActiveNav, useMobileMenu, useSmoothScrolling, useHeaderScrollEffect } from "./hooks/websiteHooks";
import { useEffect } from "react";
import { getApiBaseUrl } from "./APIClient/apiClient";

const TRACKING_SESSION_KEY = "vqb_tracking_session";
const TRACKING_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

function getOrCreateTrackingSessionId() {
  try {
    const raw = localStorage.getItem(TRACKING_SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.id && parsed?.createdAt && Date.now() - parsed.createdAt < TRACKING_SESSION_TTL_MS) {
        return parsed.id;
      }
    }
  } catch {
    // Ignore parse/storage issues and create a new session id
  }

  const newSessionId = `vqb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(
      TRACKING_SESSION_KEY,
      JSON.stringify({
        id: newSessionId,
        createdAt: Date.now(),
      }),
    );
  } catch {
    // Ignore storage failures
  }

  return newSessionId;
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize global website features
  useLanguageFeatures();
  useActiveNav();
  useMobileMenu();
  useSmoothScrolling();
  useHeaderScrollEffect();

  // Track visitor page view
  useEffect(() => {
    // Skip admin routes
    if (location.pathname.startsWith("/admin")) {
      return;
    }

    // Track page view
    const trackPageView = async () => {
      try {
        const trackingUrl = `${getApiBaseUrl()}/stats/track`;
        await fetch(trackingUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: getOrCreateTrackingSessionId(),
            page_path: location.pathname,
            referrer: document.referrer,
            user_agent: navigator.userAgent,
          }),
        });
      } catch (err) {
        // Silently fail - don't interrupt user experience
        console.debug("Tracking error:", err);
      }
    };

    trackPageView();
  }, [location.pathname]);

  // Admin keyboard shortcut: Ctrl+Shift+A
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        e.preventDefault();
        navigate("/admin/login");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />

      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="publications" element={<Publications />} />
        <Route path="open-source" element={<OpenSource />} />
        <Route path="webinars" element={<Webinars />} />
        <Route path="research" element={<Research />} />
        <Route path="members" element={<Members />} />
        <Route path="partners" element={<Partner />} />
        <Route path="donations" element={<Donations />} />
        <Route path="contact" element={<Contact />} />
        <Route path="terms" element={<Terms />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="brand" element={<Brand />} />
        <Route path="about" element={<div>About VietQubit</div>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <TranslationProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TranslationProvider>
  );
}

export default App;
